<!-- @format -->
<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import Swiper from "swiper";
  import { Navigation, Pagination } from "swiper/modules";
  import type { SwiperOptions } from "swiper/types";
  import { generateRandomID } from "~/util/common";

  export let slidesCount: number = 0;
  export let showPagination: boolean = true;
  export const maxVisibleSlides: number = 0; // 0 means no limit

  // Create event dispatcher to emit Swiper instance
  const dispatch = createEventDispatcher();

  $: id = generateRandomID();
  let swiperEl: HTMLElement | null = null;
  let swiperInstance: Swiper;
  let isInitialized = false;
  let isVisible = false;
  
  // Check for browser environment to prevent SSR issues
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  
  // Flag to prevent double navigation
  let isNavigating = false;

  const params: SwiperOptions = {
    modules: [Navigation, Pagination],
    slidesPerView: 1, // Set to 1 for mobile to ensure one-by-one navigation
    spaceBetween: 15,
    centeredSlides: false,
    loop: false,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    resizeObserver: true,
    updateOnWindowResize: true,
    slidesPerGroup: 1, // Always move one slide at a time
    breakpoints: {
      480: {
        slidesPerView: 1.2, // Start showing partial next slide on slightly larger phones
      },
      576: {
        slidesPerView: 2, // Show 2 slides on tablets/larger phones
      },
      768: {
        slidesPerView: 3, // Show 3 slides on medium screens
      },
      992: {
        slidesPerView: 4, // Show 4 slides on desktop
      }
    },
  };

  const handlePrevClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double navigation
    if (isNavigating || !swiperInstance) return;
    
    isNavigating = true;
    swiperInstance.slidePrev();
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigating = false;
    }, 300);
  };

  const handleNextClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double navigation
    if (isNavigating || !swiperInstance) return;
    
    isNavigating = true;
    swiperInstance.slideNext();
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isNavigating = false;
    }, 300);
  };

  const handleKeyDown = (e: KeyboardEvent, action: 'prev' | 'next') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent double navigation
      if (isNavigating || !swiperInstance) return;
      
      isNavigating = true;
      action === 'prev' ? swiperInstance.slidePrev() : swiperInstance.slideNext();
      
      // Reset the flag after a short delay
      setTimeout(() => {
        isNavigating = false;
      }, 300);
    }
  };

  const handleTouchStart = (e: TouchEvent) => {
    if ((e.target as HTMLElement).closest('.swiper-button-prev') || 
        (e.target as HTMLElement).closest('.swiper-button-next')) {
      e.stopPropagation();
    }
  };

  onMount(() => {
    if (!swiperEl || !isBrowser) return;
    
    const config: SwiperOptions = {
      ...params,
      pagination: showPagination ? {
        enabled: true,
        clickable: true,
        el: `#pagination-${id}`,
      } : false,
      // Disable native navigation since we're handling it manually
      navigation: false,
      on: {
        afterInit: () => {
          isInitialized = true;
          setTimeout(() => {
            updateNavigationVisibility();
            isVisible = true;
          }, 50);
        },
        slideChange: () => {
          updateNavigationVisibility();
        },
        resize: () => {
          updateNavigationVisibility();
        }
      }
    };

    setTimeout(() => {
      if (swiperEl) {
        swiperInstance = new Swiper(swiperEl, config);
        isInitialized = true;
        updateNavigationVisibility();
        
        // Dispatch the Swiper instance to the parent component
        dispatch('swiperInit', swiperInstance);
      }
    }, 50);
    
    if (isBrowser) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
    }
  });
  
  onDestroy(() => {
    if (!isBrowser) return;
    
    if (swiperInstance) {
      swiperInstance.destroy(true, true);
    }
    document.removeEventListener('touchstart', handleTouchStart);
  });

  const updateNavigationVisibility = () => {
    if (!swiperInstance || !isInitialized || !isBrowser) return;

    const prevButton = document.querySelector(`.swiper-button-prev-${id}`);
    const nextButton = document.querySelector(`.swiper-button-next-${id}`);
    
    if (prevButton) {
      if (swiperInstance.isBeginning) {
        (prevButton as HTMLElement).classList.add('swiper-button-disabled');
      } else {
        (prevButton as HTMLElement).classList.remove('swiper-button-disabled');
      }
    }

    if (nextButton) {
      if (swiperInstance.isEnd) {
        (nextButton as HTMLElement).classList.add('swiper-button-disabled');
      } else {
        (nextButton as HTMLElement).classList.remove('swiper-button-disabled');
      }
    }
  };
</script>

<div class="mylly-offer-carousel" class:is-visible={isVisible}>
  <div class="swiper-container">
    <!-- Desktop navigation buttons (hidden on mobile) -->
    <button 
      class="swiper-button-prev swiper-button-prev-{id} desktop-nav-button" 
      on:click={handlePrevClick}
      on:keydown={(e) => handleKeyDown(e, 'prev')}
      aria-label="Previous slide"
      type="button"
      style="visibility: hidden;"
    >
      <span class="visually-hidden">Previous</span>
    </button>
    
    <div class="swiper-wrapper-container">
      <div class="swiper" bind:this={swiperEl}>
        <div class="swiper-wrapper">
          <slot name="swiper" />
        </div>
      </div>
    </div>
    
    <!-- Desktop navigation buttons (hidden on mobile) -->
    <button 
      class="swiper-button-next swiper-button-next-{id} desktop-nav-button" 
      on:click={handleNextClick}
      on:keydown={(e) => handleKeyDown(e, 'next')}
      aria-label="Next slide"
      type="button"
      style="visibility: hidden;"
    >
      <span class="visually-hidden">Next</span>
    </button>
  </div>
  
  <div class="mylly-carousel-control">
    <!-- Mobile navigation buttons -->
    <div class="mobile-nav-buttons">
      <button 
        class="swiper-button-prev swiper-button-prev-{id} mobile-nav-button" 
        on:click={handlePrevClick}
        on:keydown={(e) => handleKeyDown(e, 'prev')}
        aria-label="Previous slide"
        type="button"
        style="visibility: hidden;"
      >
        <span class="visually-hidden">Previous</span>
      </button>
      <button 
        class="swiper-button-next swiper-button-next-{id} mobile-nav-button" 
        on:click={handleNextClick}
        on:keydown={(e) => handleKeyDown(e, 'next')}
        aria-label="Next slide"
        type="button"
        style="visibility: hidden;"
      >
        <span class="visually-hidden">Next</span>
      </button>
    </div>
    
    {#if showPagination}
      <div class="swiper-pagination" id="pagination-{id}"></div>
    {/if}
    <div class="mylly-link-button-container">
      <slot name="link-button" />
    </div>
  </div>
</div>

<style lang="scss">
  .mylly-offer-carousel {
    position: relative;
    padding: 60px 0 75px;
    visibility: hidden; /* Hide entire carousel until initialized */
    opacity: 0;
    transition: opacity 0.3s ease;
    margin-bottom: 0;
    
    &.is-visible {
      visibility: visible;
      opacity: 1;
    }

    /* Hide all navigation buttons by default - they will only become visible when .is-visible is applied */
    .swiper-button-prev,
    .swiper-button-next,
    .desktop-nav-button,
    .mobile-nav-button {
      visibility: hidden !important;
      opacity: 0 !important;
      transition: none !important; /* Prevent transition on initial load */
    }
    
    /* Show buttons only after initialization */
    &.is-visible {
      .swiper-button-prev,
      .swiper-button-next,
      .desktop-nav-button,
      .mobile-nav-button {
        visibility: visible !important;
        opacity: 1 !important;
        transition: opacity 0.3s ease, visibility 0.3s ease !important; /* Re-enable transitions after visible */
      }
    }

    /* Additional rule to prevent FOUC - redundant but helpful */
    &:not(.is-visible) {
      .swiper-button-prev,
      .swiper-button-next,
      .desktop-nav-button,
      .mobile-nav-button {
        visibility: hidden !important;
        opacity: 0 !important;
        display: block !important; /* Ensure layout is preserved but element is invisible */
      }
    }

    @media screen and (min-width: 576px) {
      padding: 60px 80px 75px;
    }
    
    .swiper-container {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
    }
    
    .swiper-wrapper-container {
      width: 100%;
      padding: 0 20px; /* Add horizontal padding on small screens */
      
      @media screen and (min-width: 576px) {
        padding: 0; /* Remove padding on larger screens as it's handled by the parent */
      }
    }
    
    :global(.swiper) {
      width: 100%;
      overflow: hidden; /* Contain content within the container */
      position: relative;
    }

    :global(.swiper-wrapper) {
      display: flex;
      width: 100%;
      height: auto; /* Allow height to adjust based on content */
      align-items: stretch; /* Make all slides the same height */
    }
    
    :global(.swiper-slide) {
      height: auto !important; /* Force auto height */
      display: flex !important; /* Force flex display */
      align-items: stretch !important; /* Ensure slides stretch to match tallest */
      
      /* Make the direct children (cards) take full height */
      > :global(*) {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      }
      
      /* Allow expanded cards to break out of the height constraints */
      > :global(.is-expanded) {
        position: relative;
        z-index: 100 !important; /* Ensure expanded cards are above everything */
      }
    }

    /* Base styles for all navigation buttons */
    .swiper-button-next,
    .swiper-button-prev {
      width: 35px;
      height: 35px;
      background-size: cover;
      background-position: center;
      cursor: pointer;
      z-index: 101; /* Ensure buttons are above expanded cards */
      border: none;
      padding: 0;
      
      &.swiper-button-disabled {
        opacity: 0.35;
        pointer-events: none;
      }
    }
    
    .swiper-button-prev {
      background: url('/theme/mylly2023/images/arrow-btn.svg');
    }
    
    .swiper-button-next {
      background: url('/theme/mylly2023/images/arrow-btn.svg');
      transform: rotate(180deg);
    }
    
    /* Explicitly hide Swiper's default pseudo-elements */
    :global(.swiper-button-next::after),
    :global(.swiper-button-prev::after) {
      content: '' !important;
      display: none !important;
    }
    
    /* Desktop navigation buttons (hidden on mobile) */
    .desktop-nav-button {
      display: none; /* Hide on mobile by default */
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      
      &.swiper-button-prev {
        left: -40px;
      }
      
      &.swiper-button-next {
        right: -40px;
        transform: translateY(-50%) rotate(180deg);
      }
      
      @media screen and (min-width: 576px) {
        display: block; /* Show on desktop */
        
        &.swiper-button-prev {
          left: -50px; /* Position further outside to avoid overlap */
        }
        
        &.swiper-button-next {
          right: -50px; /* Position further outside to avoid overlap */
        }
      }
    }
    
    /* Mobile navigation buttons */
    .mobile-nav-buttons {
      display: flex;
      justify-content: center;
      gap: 60px;
      margin-top: 60px; /* Increased space between cards and buttons */
      
      @media screen and (min-width: 576px) {
        display: none; /* Hide on desktop */
      }
      
      .mobile-nav-button {
        position: relative;
        display: block;
        background: url('/theme/mylly2023/images/arrow-btn.svg');
        background-size: cover;
        background-position: center;
        border-radius: 50%;
        
        &.swiper-button-next {
          transform: rotate(180deg);
        }
      }
    }
    
    .swiper-pagination {
      position: static;
      text-align: center;
      margin-top: 20px;
    }
  }
  
  .mylly-carousel-control {
    position: relative;
    margin-top: 20px;
  }
  
  .mylly-link-button-container {
    display: flex;
    justify-content: center;
    margin-top: 60px;
  }
  
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
</style>
